// ============================================================
// 📄 GERADOR DA FICHA GAL (B211) — Requisição de Exame / Biologia Médica
// ============================================================
// Reproduz, em HTML imprimível FIEL ao formulário oficial, a "Requisição de
// Exame – Biologia Médica" do Sistema Gerenciador de Ambiente Laboratorial
// (GAL/MS): brasão, faixas verticais por seção (REQUISIÇÃO / PACIENTE /
// INFORMAÇÕES CLÍNICAS / NOTIFICAÇÃO SINAN / AMOSTRA-EXAME / DADOS
// COMPLEMENTARES), os 67 campos numerados, listas de opções inline e áreas
// de preenchimento cinza com marcações nos campos de código/data.
// Dimensionada para caber em UMA página A4 (validação AI_95, rodada 2).
// Pré-preenchida com paciente (prontuário) + solicitante (cadastro) + pedido
// (modal). 100% client-side, sem biblioteca de PDF e sem logar conteúdo
// clínico (LGPD — classe B196).

import { telefoneVigente } from "../../../shared/utils/telefoneBR";
import { Paciente } from "../../../types";
import { abrirPDFEmNovaJanela } from "./gerarPDF";
import brasaoBase64 from "./brasaoBase64";
// B317 (AI_143): escapeHtml PROMOVIDA a util compartilhado (fonte única) —
// agora usada também pelos geradores centrais (gerarPDF/gerarPDFAgenda).
import { escapeHtml } from "../../../shared/utils/escapeHtml";

// Dados fixos do solicitante (vêm do cadastro do usuário + registro do conselho)
export interface SolicitanteGAL {
  nomeProfissional: string;
  registroConselho: string; // ex.: "CRM/RO 3793"
  cnsProfissional?: string;
  unidadeSaude?: string;
  cnes?: string;
  municipioAtendimento?: string;
  codigoIBGE?: string;
  ufAtendimento?: string;
}

// Dados específicos do pedido (preenchidos no modal; persistidos junto ao pedido)
export interface DadosPedidoGAL {
  exameSolicitado: string; // ex.: "Citomegalovírus (CMV) — Carga Viral / PCR Quantitativo"
  codigoSUS?: string; // ex.: "02.13.01.011-9"
  dataSolicitacao: string; // dd/mm/aaaa
  materialBiologico: string; // ex.: "Sangue total (EDTA)"
  numeroAmostra: string; // ex.: "Única"
  dataColeta?: string; // dd/mm/aaaa (opcional — laboratório pode preencher)
  finalidade?: string; // ex.: "4 - Programa"
  tipoCaso?: string; // ex.: "3 - Acompanhamento" (campo 44)
  agravoDoenca?: string; // campo 39 — ex.: "Infecção pelo Citomegalovírus (CMV)"
  dataPrimeirosSintomas?: string; // campo 40 — dd/mm/aaaa
  tratamentoQuantidade?: string; // campo 45 — quantidade (ex.: "3")
  tratamentoUnidade?: string; // campo 45 — ex.: "3 - Mês(es)"
  etapaTratamento?: string; // campo 46 — ex.: "2 - Tratamento"
  observacoes?: string; // campo 67
}

// ------------------------------------------------------------
// HELPERS
// ------------------------------------------------------------
// B317 (AI_143): re-export da fonte única (import acima) — mantém os imports
// das demais fichas SUS (CargaViral/APAC/LME/HepatiteB) inalterados.
export { escapeHtml };

/** Converte data armazenada (ISO yyyy-mm-dd ou já dd/mm/aaaa) para dd/mm/aaaa. */
export function formatarDataBR(valor: string | undefined | null): string {
  if (!valor) return "";
  const s = String(valor).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return s;
}

/**
 * Idade em anos completos a partir da data de nascimento (ISO yyyy-mm-dd ou
 * dd/mm/aaaa). `hoje` é injetável para teste. Retorna "" se a data for inválida.
 */
export function calcularIdadeAnos(
  dataNascimento: string | undefined | null,
  hoje: Date = new Date()
): string {
  if (!dataNascimento) return "";
  const s = String(dataNascimento).trim();
  let ano: number, mes: number, dia: number;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (iso) {
    ano = Number(iso[1]);
    mes = Number(iso[2]);
    dia = Number(iso[3]);
  } else if (br) {
    dia = Number(br[1]);
    mes = Number(br[2]);
    ano = Number(br[3]);
  } else {
    return "";
  }
  let idade = hoje.getFullYear() - ano;
  const aniversarioJaPassou =
    hoje.getMonth() + 1 > mes ||
    (hoje.getMonth() + 1 === mes && hoje.getDate() >= dia);
  if (!aniversarioJaPassou) idade -= 1;
  if (idade < 0 || idade > 130) return "";
  return String(idade);
}

// ------------------------------------------------------------
// CÉLULA DO FORMULÁRIO (grade de 12 colunas)
// ------------------------------------------------------------
interface OpcoesCelula {
  span?: number; // colunas da grade de 12 que ocupa
  ticks?: boolean; // área de preenchimento segmentada (códigos/datas)
  obrig?: boolean; // exibe asterisco de obrigatório
  opcoes?: string; // lista de opções oficiais (texto de referência)
}

function celula(
  num: string,
  rotulo: string,
  valor: string,
  opts: OpcoesCelula = {}
): string {
  const { span = 1, ticks = false, obrig = false, opcoes = "" } = opts;
  return `
    <div class="campo" style="grid-column: span ${span};">
      <div class="topo">
        <span class="num">${num}</span><span class="lbl">${rotulo}${
    obrig ? '<span class="ob">*</span>' : ""
  }</span>
      </div>
      ${opcoes ? `<div class="opts">${opcoes}</div>` : ""}
      <div class="fill${ticks ? " ticks" : ""}">${valor || "&nbsp;"}</div>
    </div>`;
}

const secao = (titulo: string, linhas: string[]): string => `
  <div class="secao">
    <div class="banda"><span>${titulo}</span></div>
    <div class="corpo">
      ${linhas.map((l) => `<div class="linha">${l}</div>`).join("")}
    </div>
  </div>`;

// Coluna da tabela AMOSTRA / EXAME (cabeçalho)
const colHead = (num: string, titulo: string, obrig = false): string =>
  `<th><span class="num">${num}</span> ${titulo}${
    obrig ? '<span class="ob">*</span>' : ""
  }</th>`;

// ------------------------------------------------------------
// MONTAGEM DO HTML
// ------------------------------------------------------------
// Devolve apenas o MIOLO da ficha (o bloco .ficha), sem <html>/<style>, para
// poder ser (a) embrulhado no documento autônomo OU (b) anexado ao pedido de
// exames comum no mesmo documento de impressão (1 conjunto: pedido + GAL).
function corpoFichaGAL(
  paciente: Paciente,
  solicitante: SolicitanteGAL,
  pedido: DadosPedidoGAL,
  // B289 (AI_127) — selo ICP-Brasil (compacto) no campo 10 (assinatura do
  // profissional solicitante), só no PDF assinado. Sem selo = campo em branco.
  selo?: string
): string {
  // ===== REQUISIÇÃO (1-13) =====
  const req = [
    celula("1", "Nº Requisição:", "", { span: 4 }) +
      celula("2", "Unidade de Saúde (ou outra fonte):", escapeHtml(solicitante.unidadeSaude), { span: 5, obrig: true }) +
      celula("3", "CNES:", escapeHtml(solicitante.cnes), { span: 3, ticks: true, obrig: true }),
    celula("4", "Município de Atendimento:", escapeHtml(solicitante.municipioAtendimento), { span: 9 }) +
      celula("5", "Código IBGE:", escapeHtml(solicitante.codigoIBGE), { span: 2, ticks: true, obrig: true }) +
      celula("6", "UF:", escapeHtml(solicitante.ufAtendimento), { span: 1 }),
    celula("7", "CNS Prof. de Saúde:", escapeHtml(solicitante.cnsProfissional), { span: 3 }) +
      celula("8", "Nome do Profissional de Saúde:", escapeHtml(solicitante.nomeProfissional), { span: 4, obrig: true }) +
      celula("9", "Registro Conselho/Matrícula:", escapeHtml(solicitante.registroConselho), { span: 2, ticks: true, obrig: true }) +
      celula("10", "Assinatura:", selo || "", { span: 3 }),
    celula("11", "Data de Solicitação:", escapeHtml(pedido.dataSolicitacao), { span: 4, ticks: true, obrig: true }) +
      celula("12", "Finalidade:", escapeHtml(pedido.finalidade), {
        span: 5,
        opcoes:
          "1-Campanha 2-Inquérito 3-Investigação · 4-Programa 5-Protocolo 6-Projeto 9-Ignorado",
      }) +
      celula("13", "Descrição:", escapeHtml(pedido.exameSolicitado), { span: 3 }),
  ];

  // ===== PACIENTE (14-38) =====
  const idadeAnos = calcularIdadeAnos(paciente.dataNascimento);
  const cnsPaciente = paciente.cns || paciente.hospub; // hospub = Cartão SUS legado
  const pac = [
    celula("14", "Tipo Paciente:", "1 - Brasileiro", {
      span: 5,
      opcoes: "1-Brasileiro 2-Estrangeiro 3-Indígena 4-Vulnerável",
    }) +
      celula("15", "CPF do paciente:", escapeHtml(paciente.cpf), { span: 7, obrig: true }),
    celula("16", "CNS do paciente:", escapeHtml(cnsPaciente), { span: 5, obrig: true }) +
      celula("17", "Nome do Paciente:", escapeHtml(paciente.nome), { span: 7, obrig: true }),
    celula("18", "Data de Nascimento:", formatarDataBR(paciente.dataNascimento), { span: 4, ticks: true, obrig: true }) +
      celula("19", "Idade:", idadeAnos ? `${idadeAnos} · 4 - Ano(s)` : "", { span: 3, obrig: true, opcoes: "Qtd. · 1-Hora 2-Dia 3-Mês 4-Ano" }) +
      celula("20", "Sexo:", paciente.sexo === "F" ? "F - Feminino" : paciente.sexo === "M" ? "M - Masculino" : "", { span: 3, obrig: true, opcoes: "M-Masculino F-Feminino I-Ignorado" }) +
      celula("21", "Nacionalidade:", "", { span: 2 }),
    celula("22", "Raça/Cor:", "", {
      span: 5,
      opcoes: "1-Branca 2-Preta 3-Parda 4-Amarela 5-Indígena 99-Sem Informação",
    }) +
      celula("23", "Etnia:", "", { span: 3 }) +
      celula("24", "Nome da Mãe:", escapeHtml(paciente.nomeMae), { span: 4 }),
    celula("25", "Documento 1:", paciente.rg ? `1 - RG · nº ${escapeHtml(paciente.rg)}` : "", { span: 6, opcoes: "1-RG 2-CNH 3-CNS 4-CNASC 5-PRONT 6-INFOPEN — Número:" }) +
      celula("26", "Documento 2:", "", { span: 6, opcoes: "1-RG 2-CNH 3-CNS 4-CNASC 5-PRONT 6-INFOPEN — Número:" }),
    celula("27", "Endereço do paciente: (Rua, Avenida...)", escapeHtml(paciente.logradouro), { span: 10 }) +
      celula("28", "Número:", escapeHtml(paciente.numero), { span: 2 }),
    celula("29", "Complemento do endereço:", escapeHtml(paciente.complemento), { span: 5 }) +
      celula("30", "Ponto de Referência:", "", { span: 4 }) +
      celula("31", "Bairro:", escapeHtml(paciente.bairro), { span: 3 }),
    celula("32", "Município de Residência:", escapeHtml(paciente.cidade), { span: 9, obrig: true }) +
      celula("33", "Código IBGE:", "", { span: 2, ticks: true, obrig: true }) +
      celula("34", "UF:", escapeHtml(paciente.estado), { span: 1 }),
    celula("35", "CEP:", escapeHtml(paciente.cep), { span: 3, ticks: true }) +
      celula("36", "DDD / Telefone:", escapeHtml(telefoneVigente(paciente)), { span: 4 }) +
      celula("37", "Zona:", "", { span: 3, opcoes: "1-Urbana 2-Periurbana 3-Rural 4-Silvestre 9-Ignorada" }) +
      celula("38", "País (Se reside fora do Brasil):", "", { span: 2, obrig: true }),
  ];

  // ===== INFORMAÇÕES CLÍNICAS (39-49) =====
  const tratamento = [
    escapeHtml(pedido.tratamentoQuantidade),
    escapeHtml(pedido.tratamentoUnidade),
  ]
    .filter(Boolean)
    .join(" · ");
  const clin = [
    celula("39", "Agravo/Doença:", escapeHtml(pedido.agravoDoenca), { span: 8 }) +
      celula("40", "Data dos Primeiros Sintomas:", escapeHtml(pedido.dataPrimeirosSintomas), { span: 4, ticks: true }),
    celula("41", "Idade Gestacional:", "", {
      span: 5,
      opcoes: "1-1ºTrim 2-2ºTrim 3-3ºTrim 4-Ignorada · 5-Não 6-Não se Aplica 9-Ignorado",
    }) +
      celula("42", "Motivo:", "", { span: 4 }) +
      celula("43", "Diagnóstico:", "", { span: 3 }),
    celula("44", "Caso:", escapeHtml(pedido.tipoCaso), {
      span: 5,
      opcoes:
        "1-Suspeito 2-Comunicante 3-Acompanhamento 4-Controle 5-Óbito 6-Caso grave 7-Surto 8-Diagnóstico 9-Ignorado",
    }) +
      celula("45", "Tratamento:", tratamento, { span: 3, opcoes: "Qtd. · 1-Dia 2-Semana 3-Mês 4-Ano 9-Ignorado" }) +
      celula("46", "Etapa de Tratamento:", escapeHtml(pedido.etapaTratamento), { span: 4, opcoes: "1-Pretratamento 2-Tratamento 3-Retratamento 4-Aval. Resistência 9-Ignorado" }),
    celula("47", "Paciente Tomou Vacina?", "", { span: 5, opcoes: "1-Sim 2-Não 9-Ignorado" }) +
      celula("48", "Vacina?", "", { span: 3 }) +
      celula("49", "Data da Última Dose:", "", { span: 4, ticks: true }),
  ];

  // ===== NOTIFICAÇÃO SINAN (50-58) =====
  const sinan = [
    celula("50", "Agravo/Doença de notificação do SINAN:", "", { span: 6 }) +
      celula("51", "CID10:", "", { span: 2, ticks: true, obrig: true }) +
      celula("52", "Nº Notificação do SINAN:", "", { span: 2, ticks: true, obrig: true }) +
      celula("53", "Data de Notificação:", "", { span: 2, ticks: true, obrig: true }),
    celula("54", "Unidade de Saúde Notificante:", "", { span: 10 }) +
      celula("55", "CNES", "", { span: 2, ticks: true, obrig: true }),
    celula("56", "Município de Notificação:", "", { span: 9 }) +
      celula("57", "Código IBGE", "", { span: 2, ticks: true, obrig: true }) +
      celula("58", "UF:", "", { span: 1 }),
  ];

  // ===== AMOSTRA / EXAME (59-66) — TABELA (1 linha p/ caber em 1 página) =====
  const tabelaAmostra = `
    <div class="secao">
      <div class="banda"><span>AMOSTRA / EXAME</span></div>
      <div class="corpo">
        <table class="amostra">
          <colgroup>
            <col style="width:22%" />
            <col style="width:12%" />
            <col style="width:8%" />
            <col style="width:6%" />
            <col style="width:7%" />
            <col style="width:14%" />
            <col style="width:8%" />
            <col style="width:23%" />
          </colgroup>
          <thead>
            <tr>
              ${colHead("59", "Exame Solicitado:", true)}
              ${colHead("60", "Material Biológico:", true)}
              ${colHead("61", "Localização:")}
              ${colHead("62", "Amostra: (1ª,2ª,3ª,Única)", true)}
              ${colHead("63", "Mat.: 1-IN 2-IB 3-LM 4-MTB 5-MTV 6-FF 7-FA 8-FO 9-BF 10-PF")}
              ${colHead("64", "Data da coleta:", true)}
              ${colHead("65", "Hora da coleta:")}
              ${colHead("66", "Usou medicamento antes da coleta?")}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="fill">${escapeHtml(pedido.exameSolicitado)}${pedido.codigoSUS ? `<br/><span class="cod">(${escapeHtml(pedido.codigoSUS)})</span>` : ""}</td>
              <td class="fill">${escapeHtml(pedido.materialBiologico)}</td>
              <td class="fill">&nbsp;</td>
              <td class="fill">${escapeHtml(pedido.numeroAmostra)}</td>
              <td class="fill">&nbsp;</td>
              <td class="fill ticks">${escapeHtml(pedido.dataColeta) || "&nbsp;"}</td>
              <td class="fill ticks4">&nbsp;</td>
              <td class="med"><span class="opts">1-Sim 2-Não 9-Ignorado</span><div class="med-l">Especifique: <span class="med-d">Data início do uso:</span></div></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>`;

  // ===== DADOS COMPLEMENTARES (67) =====
  const complementares = `
    <div class="secao">
      <div class="banda banda-pequena"><span>DADOS COMPLEMENTARES</span></div>
      <div class="corpo">
        <div class="campo" style="border-right:none;">
          <div class="topo"><span class="num">67</span><span class="lbl">Observações:</span></div>
          <div class="fill" style="min-height:26px;">${escapeHtml(pedido.observacoes) || "&nbsp;"}</div>
        </div>
      </div>
    </div>`;

  return `
  <div class="ficha">
    <div class="cab">
      <div class="orgao">
        <img src="${brasaoBase64}" alt="Brasão da República" />
        <div class="txt">República Federativa do Brasil<br/>Ministério da Saúde</div>
      </div>
      <div class="sistema">
        <div class="gal">Sistema Gerenciador de Ambiente Laboratorial - GAL</div>
        <div class="req">Requisição de Exame - Biologia Médica</div>
      </div>
    </div>

    ${secao("REQUISIÇÃO", req)}
    ${secao("PACIENTE", pac)}
    ${secao("INFORMAÇÕES CLÍNICAS", clin)}
    ${secao("NOTIFICAÇÃO SINAN", sinan)}
    ${tabelaAmostra}
    ${complementares}

    <div class="gal-rodape">CGLAB/SVS/MS · Gerado por hepato.app — confira os dados antes de entregar ao laboratório.</div>
  </div>`;
}

// CSS estrutural da ficha (sem @page / body / barra-botoes) — reusável tanto
// no documento autônomo quanto embutido no pedido de exames.
const ESTILOS_FICHA_GAL = `
    .ficha { background: #fff; padding: 4mm; }
    /* CABEÇALHO */
    .cab { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
    .cab .orgao { display: flex; align-items: center; gap: 8px; }
    .cab .orgao img { width: 38px; height: 38px; }
    .cab .orgao .txt { font-weight: bold; font-size: 13px; line-height: 1.15; }
    .cab .sistema { text-align: right; }
    .cab .sistema .gal { font-weight: bold; font-size: 13px; }
    .cab .sistema .req {
      background: #b9b9b9; color: #fff; font-weight: bold; font-size: 12px;
      padding: 2px 14px; margin-top: 2px; letter-spacing: .5px;
    }
    /* SEÇÕES */
    .secao { display: flex; border-left: 1.2px solid #000; border-top: 1.2px solid #000; }
    .secao:last-of-type { border-bottom: 1.2px solid #000; }
    .banda {
      width: 18px; flex: 0 0 18px; background: #ededed;
      border-right: 1px solid #000;
      display: flex; align-items: center; justify-content: center;
    }
    .banda span {
      writing-mode: vertical-rl; transform: rotate(180deg);
      font-weight: bold; font-size: 10px; letter-spacing: 1px; white-space: nowrap;
    }
    .banda-pequena span { font-size: 7px; letter-spacing: .5px; }
    .corpo { flex: 1; min-width: 0; }
    .linha { display: grid; grid-template-columns: repeat(12, 1fr); }
    .campo {
      border-right: 1px solid #000; border-bottom: 1px solid #000;
      padding: 1px 3px 1px; min-width: 0; display: flex; flex-direction: column;
    }
    .topo { display: flex; align-items: baseline; gap: 3px; }
    .num {
      display: inline-block; border: 1px solid #000;
      font-size: 7px; line-height: 1; padding: 1px 2px; font-weight: bold; flex: 0 0 auto;
    }
    .lbl { font-size: 8.5px; font-weight: normal; }
    .ob { font-weight: bold; }
    .opts { font-size: 7px; color: #222; font-weight: normal; line-height: 1.15; margin-top: 1px; }
    .cod { font-weight: normal; color: #333; font-size: 8.5px; }
    .fill {
      margin-top: 1px; min-height: 13px; flex: 1;
      background: linear-gradient(#e2e2e2, #fbfbfb);
      font-size: 11px; font-weight: 600; padding: 1px 3px; overflow: hidden;
    }
    .fill.ticks {
      background-image:
        repeating-linear-gradient(to right, #b6b6b6 0, #b6b6b6 1px, transparent 1px, transparent 15px),
        linear-gradient(#e2e2e2, #fbfbfb);
    }
    /* hora da coleta: exatamente 4 segmentos (h h : m m) */
    .fill.ticks4 {
      background-image:
        repeating-linear-gradient(to right, #b6b6b6 0, #b6b6b6 1px, transparent 1px, transparent 25%),
        linear-gradient(#e2e2e2, #fbfbfb);
    }
    /* TABELA AMOSTRA/EXAME */
    table.amostra { width: 100%; border-collapse: collapse; table-layout: fixed; }
    table.amostra th {
      border-right: 1px solid #000; border-bottom: 1px solid #000;
      font-size: 7.5px; font-weight: normal; text-align: left; vertical-align: top;
      padding: 1px 2px; line-height: 1.15;
    }
    table.amostra th:last-child, table.amostra td:last-child { border-right: none; }
    table.amostra td {
      border-right: 1px solid #000; border-bottom: 1px solid #000;
      height: 26px; vertical-align: top; padding: 1px 2px;
      font-size: 9.5px; font-weight: 600;
    }
    table.amostra td.med { font-weight: normal; }
    table.amostra .med-l { font-size: 7.5px; color: #333; margin-top: 3px; }
    table.amostra .med-d { display: inline-block; margin-left: 8px; }
    .gal-rodape { font-size: 7.5px; color: #444; margin-top: 3px; text-align: right; }
`;

// ------------------------------------------------------------
// PONTOS DE ENTRADA
// ------------------------------------------------------------
/**
 * Fragmento da ficha GAL para ANEXAR a outro documento de impressão (pedido
 * de exames comum). Devolve os estilos (já escopados em classes próprias) e o
 * corpo embrulhado numa "folha" com página nomeada `gal` (margem 5mm),
 * forçando quebra de página antes — sai sempre numa folha nova após o pedido.
 */
export function montarFragmentoFichaGAL(
  paciente: Paciente,
  solicitante: SolicitanteGAL,
  pedido: DadosPedidoGAL,
  // B289 (AI_127) — selo ICP-Brasil (compacto) no campo 10, só no PDF assinado.
  selo?: string
): { estilos: string; corpo: string } {
  const estilos = `
    @page gal { size: A4 portrait; margin: 5mm; }
    .folha-gal { page: gal; page-break-before: always; }
    ${ESTILOS_FICHA_GAL}
    .folha-gal .ficha { width: auto; margin: 0; padding: 0; }
  `;
  const corpo = `<div class="folha-gal">${corpoFichaGAL(
    paciente,
    solicitante,
    pedido,
    selo
  )}</div>`;
  return { estilos, corpo };
}

/** Documento AUTÔNOMO da ficha GAL (apenas a ficha, sem pedido). */
function montarHTML(
  paciente: Paciente,
  solicitante: SolicitanteGAL,
  pedido: DadosPedidoGAL
): string {
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <title>Ficha GAL — ${escapeHtml(paciente.nome)}</title>
  <style>
    @page { size: A4 portrait; margin: 5mm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, Helvetica, sans-serif; color: #000; margin: 0; background: #e9e9e9; }
    .barra-botoes { text-align: center; padding: 14px; }
    .ficha { width: 200mm; margin: 0 auto 20px; }
    ${ESTILOS_FICHA_GAL}
    @media print {
      body { background: #fff; }
      .ficha { width: auto; margin: 0; padding: 0; }
      .barra-botoes { display: none; }
    }
  </style>
</head>
<body>
  <div class="barra-botoes">
    <button onclick="window.print()" style="padding: 10px 32px; background: #2563eb; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 8px;">
      🖨️ Imprimir / Salvar PDF
    </button>
    <button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>
  </div>
  ${corpoFichaGAL(paciente, solicitante, pedido)}
</body>
</html>`;
}

// ------------------------------------------------------------
// API PÚBLICA
// ------------------------------------------------------------
export function gerarFichaGAL(
  paciente: Paciente,
  solicitante: SolicitanteGAL,
  pedido: DadosPedidoGAL
): void {
  const html = montarHTML(paciente, solicitante, pedido);
  abrirPDFEmNovaJanela(html);
}
